// Domain detection for admin.getlooma.app vs dashboard.getlooma.app

const ADMIN_HOSTNAME = 'admin.getlooma.app';
const DASHBOARD_HOSTNAME = 'dashboard.getlooma.app';

// Preview/QA only: lets Tom simulate either domain on a Vercel preview URL
// (e.g. msass-xxxx.vercel.app), where the real hostname check never matches.
// Sticky in sessionStorage for the tab so it survives client-side navigations
// (react-router's navigate() drops the query string) — a raw ?test_admin=true
// check re-read on every call would stop working the moment the app redirects
// to /admin/dashboard. Never consulted on the two real hostnames, so it can't
// affect production behavior.
const TEST_MODE_KEY = 'looma-domain-test-mode';

function isKnownHostname(): boolean {
  return window.location.hostname === ADMIN_HOSTNAME || window.location.hostname === DASHBOARD_HOSTNAME;
}

function getDomainTestOverride(): 'admin' | 'dashboard' | null {
  if (isKnownHostname()) return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('test_admin') === 'true') {
    try { sessionStorage.setItem(TEST_MODE_KEY, 'admin'); } catch { /* ignore */ }
    return 'admin';
  }
  if (params.get('test_dashboard') === 'true') {
    try { sessionStorage.setItem(TEST_MODE_KEY, 'dashboard'); } catch { /* ignore */ }
    return 'dashboard';
  }
  try {
    const stored = sessionStorage.getItem(TEST_MODE_KEY);
    if (stored === 'admin' || stored === 'dashboard') return stored;
  } catch { /* ignore */ }
  return null;
}

export function isAdminDomain(): boolean {
  const override = getDomainTestOverride();
  if (override) return override === 'admin';
  if (window.location.hostname === ADMIN_HOSTNAME) return true;
  // Dev mode: ?admin=1 query param to test admin domain behavior
  if (import.meta.env.DEV && new URLSearchParams(window.location.search).has('admin')) return true;
  return false;
}

export function getDashboardUrl(path = '/login'): string {
  if (isKnownHostname()) return `https://${DASHBOARD_HOSTNAME}${path}`;
  // Dev or preview: stay on this origin. Any active test-mode override
  // persists on its own via sessionStorage, so nothing to append here.
  return path;
}

export function redirectToDashboard(path = '/login'): void {
  window.location.href = getDashboardUrl(path);
}

export function getAdminUrl(path = '/login'): string {
  if (isKnownHostname()) return `https://${ADMIN_HOSTNAME}${path}`;
  if (import.meta.env.DEV) {
    // Dev fallback for the legacy ?admin=1 flow (not persisted to sessionStorage)
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}admin=1`;
  }
  // Preview: stay on this origin — sessionStorage override persists on its own
  return path;
}

export function redirectToAdmin(path = '/login'): void {
  window.location.href = getAdminUrl(path);
}
